import {Dropdown, IconButton, openDialog, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {ActivePreset, Preset} from '../../../../types/Api';
import {isPresetCustom} from '../../../../utils/isPresetCustom';
import {EditPresetDialog} from '../../../PresetPanel/components/EditPresetDialog/EditPresetDialog';
import {PresetButtonActions} from '../../../PresetPanel/components/PresetButtonActions/PresetButtonActions';
import {PresetApiStateEnum} from '../../../PresetPanel/constants/PresetApiStateEnum';
import {wrapPresetApiCall} from '../../../PresetPanel/hooks/usePresetApiState';

export type MobilePresetsDialogProps = TestIdProps & {
  presets: Preset[];
  activePreset: ActivePreset;
  isPresetChanged: boolean;
  onActivate: (preset: Preset) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onCreate: (name: string, desc: string) => Promise<void>;
  onUpdate: (presetId: string) => void;
  onDelete: (presetId: string) => void;
  onDuplicate: (presetId: string) => void;
  onApplyToDefault: (presetId: string, saveCurrentPreset?: boolean) => Promise<void>;
  onRename: (presetId: string, title: string, description: string) => Promise<void>;
  onResetOverrides: () => void;
  onClose?: () => void;
};

/**
 * Preset panel content rendered inside the mobile sidebar.
 *
 * Shows:
 * - An "Add preset" row at the top.
 * - A scrollable list of all presets. The active preset is highlighted in blue.
 *   Tapping a row activates / deactivates it.
 * - Per-preset `Dropdown` with `PresetButtonActions` (update, reset, edit, duplicate,
 *   reorder, delete) — same actions as the desktop pill overflow menu.
 */
export function MobilePresetsDialog(props: MobilePresetsDialogProps) {
  const t = useTranslationContext();
  const activePresetId = isPresetCustom(props.activePreset) ? props.activePreset.id : undefined;
  const [apiState, setApiState] = useState<Record<string, PresetApiStateEnum>>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isPresetChangeDisabled, disabledPresetChange, allowPresetChange] = useBoolean();

  const applyPresetToVirtual = (
    presetId: string,
    saveCurrentPreset?: boolean,
    onSuccess?: () => void
  ) =>
    wrapPresetApiCall(
      presetId,
      () => props.onApplyToDefault(presetId, saveCurrentPreset),
      {setApiState, disabledPresetChange, allowPresetChange},
      {shouldDisableChanges: true, onSuccess}
    );

  return (
    <div className="flex flex-col h-full">
      {/* Add preset row */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <IconButton
          icon="content/add"
          size="small"
          isDisabled={isPresetChangeDisabled}
          onClick={() =>
            openDialog(
              <EditPresetDialog
                onSubmit={props.onCreate}
                data-testid={suffixTestId('createPreset', props)}
              />,
              {
                id: 'createPreset',
                title: t('general.presets.createPreset'),
                'data-testid': suffixTestId('createPreset', props),
                size: 'small',
                scrollBehavior: 'outside',
              }
            )
          }
          data-testid={suffixTestId('addPresetButton', props)}
        />
        <span className="text-sm text-gray-500">{t('general.presets.createPreset')}</span>
      </div>

      {/* Preset list */}
      <div className="flex flex-col gap-0.5 overflow-y-auto flex-1 px-2 py-2">
        {props.presets.map((preset, _index) => {
          const isActive = preset.id === activePresetId;

          return (
            <div
              key={preset.id}
              className={[
                'flex items-center gap-2 px-2 py-2 rounded transition-colors',
                isActive ? 'bg-blue-50' : 'hover:bg-gray-50',
              ].join(' ')}
            >
              <button
                type="button"
                className="flex-1 text-left text-sm truncate"
                onClick={() => {
                  if (isPresetChangeDisabled) {
                    return;
                  }
                  if (isActive) {
                    props.onDeactivate();
                  } else {
                    props.onActivate(preset);
                  }
                }}
              >
                <span className={isActive ? 'text-blue-700 font-medium' : 'text-gray-700'}>
                  {preset.title}
                </span>
              </button>

              <Dropdown
                isLazy
                strategy="fixed"
                isOpen={openMenuId === preset.id}
                onOpen={() => setOpenMenuId(preset.id)}
                onClose={() => {
                  if (apiState[preset.id] !== PresetApiStateEnum.loading) {
                    setOpenMenuId(null);
                  }
                }}
                dropdownControl={
                  <IconButton
                    icon="navigation/more_vert"
                    size="small"
                    data-testid={suffixTestId(`preset-${preset.title}-actions`, props)}
                  />
                }
                data-testid={suffixTestId(`preset-${preset.title}-dropdown`, props)}
              >
                <Show when={openMenuId === preset.id}>
                  <PresetButtonActions
                    preset={preset}
                    presetChanged={props.isPresetChanged}
                    activePresetId={activePresetId}
                    updatePreset={props.onUpdate}
                    deletePreset={props.onDelete}
                    duplicatePreset={props.onDuplicate}
                    applyPresetToVirtual={applyPresetToVirtual}
                    onCopySuccess={() => {
                      setOpenMenuId(null);
                      props.onClose?.();
                    }}
                    applyToDefaultDisabled={isPresetChangeDisabled}
                    applyToDefaultState={apiState[preset.id]}
                    resetOverrides={props.onResetOverrides}
                    openEditModal={() =>
                      openDialog(
                        <EditPresetDialog
                          presetToEdit={preset}
                          onSubmit={(title, description) =>
                            props.onRename(preset.id, title, description)
                          }
                          data-testid={suffixTestId('editPreset', props)}
                        />,
                        {
                          id: 'editPreset',
                          title: t('general.presets.editPreset', {presetName: preset.title}),
                          'data-testid': suffixTestId('editPreset', props),
                          size: 'small',
                          scrollBehavior: 'outside',
                        }
                      )
                    }
                    data-testid={suffixTestId(`preset-${preset.title}`, props)}
                  />
                </Show>
              </Dropdown>
            </div>
          );
        })}

        <Show when={props.presets.length === 0}>
          <div className="text-sm text-gray-400 text-center py-6">
            {t('page.datagrid.labels.noPresets')}
          </div>
        </Show>
      </div>
    </div>
  );
}
